/**
 * Tracks the download progress of an image by fetching it via the Streams API.
 * Returns a blob URL once fully downloaded, along with live progress stats.
 *
 * Usage:
 *   const { blobUrl, isLoading, loadedBytes, totalBytes, bytesPerSecond, etaSeconds } =
 *     useImageProgress(srcRef, shouldLoadRef);
 *
 * Set the resulting `blobUrl` as the <img src> to display the image.
 * When the src changes (new image), the previous blob URL is revoked automatically.
 */

import { ref, watch, onUnmounted, type Ref } from 'vue';

export interface ImageProgressState {
  /** Blob URL of the downloaded image; empty string while loading. */
  blobUrl: Ref<string>;
  /** True while fetching. */
  isLoading: Ref<boolean>;
  /** 0–100. 0 when totalBytes unknown and still loading. */
  progress: Ref<number>;
  /** Bytes received so far. */
  loadedBytes: Ref<number>;
  /** Total file size in bytes (0 if Content-Length was not present). */
  totalBytes: Ref<number>;
  /** Current throughput in bytes/second. */
  bytesPerSecond: Ref<number>;
  /** Estimated seconds remaining, or null if unknown. */
  etaSeconds: Ref<number | null>;
  /** Set if the fetch failed (non-abort). */
  error: Ref<Error | null>;
}

const SPEED_WINDOW_MS = 400;

export function useImageProgress(
  src: Ref<string>,
  enabled: Ref<boolean>,
): ImageProgressState {
  const blobUrl = ref('');
  const isLoading = ref(false);
  const progress = ref(0);
  const loadedBytes = ref(0);
  const totalBytes = ref(0);
  const bytesPerSecond = ref(0);
  const etaSeconds = ref<number | null>(null);
  const error = ref<Error | null>(null);

  let abortController: AbortController | null = null;
  let activeBlobUrl = '';

  function reset() {
    if (abortController) {
      abortController.abort();
      abortController = null;
    }
    if (activeBlobUrl) {
      URL.revokeObjectURL(activeBlobUrl);
      activeBlobUrl = '';
    }
    blobUrl.value = '';
    isLoading.value = false;
    progress.value = 0;
    loadedBytes.value = 0;
    totalBytes.value = 0;
    bytesPerSecond.value = 0;
    etaSeconds.value = null;
    error.value = null;
  }

  async function load(url: string) {
    reset();

    const controller = new AbortController();
    abortController = controller;
    isLoading.value = true;

    const startTime = performance.now();
    let lastWindowTime = startTime;
    let lastWindowBytes = 0;

    try {
      const response = await fetch(url, { signal: controller.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      if (!response.body) throw new Error('Response has no body');

      const contentLength = parseInt(response.headers.get('Content-Length') ?? '0', 10) || 0;
      totalBytes.value = contentLength;

      const chunks: Uint8Array[] = [];
      const reader = response.body.getReader();

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        loadedBytes.value += value.byteLength;

        if (contentLength > 0) {
          progress.value = Math.min(100, (loadedBytes.value / contentLength) * 100);
        }

        // Rolling speed window
        const now = performance.now();
        const windowMs = now - lastWindowTime;
        if (windowMs >= SPEED_WINDOW_MS) {
          const deltaBytes = loadedBytes.value - lastWindowBytes;
          const speed = deltaBytes / (windowMs / 1000);
          bytesPerSecond.value = speed;
          if (speed > 0 && contentLength > 0) {
            etaSeconds.value = (contentLength - loadedBytes.value) / speed;
          }
          lastWindowTime = now;
          lastWindowBytes = loadedBytes.value;
        }
      }

      // Assemble and expose the blob URL
      const blob = new Blob(chunks);
      const objUrl = URL.createObjectURL(blob);
      activeBlobUrl = objUrl;
      blobUrl.value = objUrl;
      progress.value = 100;
      isLoading.value = false;
      bytesPerSecond.value = 0;
      etaSeconds.value = null;
    } catch (e) {
      if ((e as Error).name !== 'AbortError') {
        error.value = e as Error;
        isLoading.value = false;
      }
    }
  }

  watch(
    [src, enabled] as const,
    ([newSrc, isEnabled]) => {
      if (isEnabled && newSrc) {
        load(newSrc);
      } else {
        reset();
      }
    },
    { immediate: true },
  );

  onUnmounted(reset);

  return {
 blobUrl,
isLoading,
progress,
loadedBytes,
totalBytes,
bytesPerSecond,
etaSeconds,
error,
};
}
